import Service from '@ember/service';

import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import { service } from '@ember/service';
import { DEBUG } from '@glimmer/env';
import { cached, tracked } from '@glimmer/tracking';

import { createGraphResult, GraphResult } from 'rise-ui/utils/graph-result.js';
import { default as LoaderOrderer } from 'rise-ui/utils/least-loaded-loader-orderer.js';
import { readOnly } from 'rise-ui/utils/object/computed.js';
import { collect } from 'rise-ui/utils/object/computed/array.js';

/**
 * Loads time-series results queries into cached GraphObjects
 * @name GraphResultsService
 */
export default class TimeSeriesGraphResultsService extends Service {
  @service router;
  @service store;
  @service('time-series/outputs') timeSeriesOutputs;
  @service('time-series/dates') timeSeriesDates;
  @service('debug/time-series-output') debugTimeSeriesOutput;

  loaderOrderer = new LoaderOrderer();

  @collect('timeSeriesOutputs.outputs', ...(!DEBUG?[]:['debugTimeSeriesOutput.outputs'])) _outputs;
  // Stores cached output graph results
  _graphResultsByKey = {};

  @cached get outputs() {
    if (this.store.isDestroyed) {
      return Promise.reject();
    }
    const { _graphResultsByKey } = this;
    const { rootURL } = this.router;
    const graphResultsByKey = {};
    const modelRuns = [];
    const outputs = this._outputs.map(output => {
      let key, result;
      if (output instanceof GraphResult) {
        key = output.key;
        result = output;
      } else {
        const { modelName } = output?.constructor || {};
        const isItem = modelName === 'item';
        const query = { };
        let dates;
        if (isItem) {
          query.itemId = output.id;
          dates = this.itemDateRanges[output.id];
        } else {
          query.modelRunId = output.id;
          dates = this.modelRunDateRanges[output.id];
          modelRuns.push(output.id);
        }
        dates = dates?.[0];
        if (dates) {
          query.datesRanges = dates;
        }

        key = `${modelName}.${output.id}.${JSON.stringify(dates)}`;
        result = _graphResultsByKey[key];
        if (!result) {
          result = createGraphResult({
            key, output, dates, rootURL, query, owner: getOwner(this),
          });
        }
      }
      graphResultsByKey[key] = result;
      return result;
    });
    if (modelRuns.length) {
      this.store.query('model-run-member', {
        modelRunId: modelRuns.join(','),
        itemsPerPage: 1000,
        [`order[createDate]`]: 'ASC',
        [`order[id]`]: 'ASC',
      }).then(modelRunMembers => {
        const mrms = {};
        modelRunMembers.forEach(modelRunMember => {
          const modelRunId = modelRunMember.belongsTo('modelRun').id();
          const mrm = mrms[modelRunId] || [];
          mrm.push(modelRunMember);
          mrms[modelRunId] = mrm;
        });
        this.outputs.forEach(graphResult => {
          const { output } = graphResult;
          const modelRunId = output.id;
          const { modelName } = output?.constructor || {};
          const isModelRun = modelName !== 'item';
          if (isModelRun && mrms[modelRunId]) {
            graphResult.meta.modelRunMembers = mrms[modelRunId];
          }
        });
      });
    }
    this._graphResultsByKey = graphResultsByKey;
    return outputs;
  }

  @readOnly('timeSeriesDates.itemDates') itemDateRanges;
  @readOnly('timeSeriesDates.modelRunDates') modelRunDateRanges;

  @readOnly('timeSeriesOutputs.isLoading') isLoading;

  @readOnly('timeSeriesOutputs.isLoading') isLoadingOutputs;

  @tracked loading = false;
  @tracked error = null;

  get all() {
    let all = {
      loaded:  true,
      loading: false,
      paused:  false,
      error:   false,
      none:    true,
    };
    this.outputs.forEach(loader => {
      const { complete, error, loading, output, paused } = loader;
      if (output.$hasInvalidDates) {
        return;
      }
      all = {
        loaded:  all.loaded  && complete,
        loading: all.loading || !complete && loading,
        paused:  all.paused  || paused,
        error:   all.error   || error,
        none:    false,
      };
    });
    return all;
  }
  @readOnly('all.none') isNone;
  @readOnly('all.loaded') isAllLoaded;
  @readOnly('all.loading') isSomeLoading;
  @readOnly('all.paused') isSomePaused;
  @readOnly('all.error') isSomeError;

  @action next() {
    if (this.loading) {
      return;
    }
    const outputs = this.outputs;
    const index = this.loaderOrderer.next(outputs);
    if (index || index === 0) {
      const output = outputs[index];
      const loader = output.loader;
      const next = loader.next();
      if (next) {
        this.loading = true;
        this.error = null;
        output.error = null;
        next
          .catch(error => {
            console.error(error);
            this.error = error;
            output.error = error;
            output.loading = false;
          })
          .then(results => {
            if (loader.complete) {
              output.loading = false;
            }
            output.results.pushObjects(results);
          })
          .finally(() => {
            this.loading = false;
            this.next();
          });
        this.next();
      } else {
        this.loading = false;
      }
    }
  }

  @action toggle(...outputs) {
    const value = !this.isSomeLoading;
    GraphResult.all.toggle(outputs, value);
    if (value) {
      this.next();
    }
  }

  @action toggleAll() {
    this.toggle(...this.outputs);
  }
}
